/**
 * Left-nav grey items must say why in ordinary language (#915) when a
 * project is open — either the item works, or a short reason is visible.
 *
 * Book/project doors stay disabled when no project is open (#969):
 * Back to Projects, a deleted current project, or a leftover id that
 * is no longer in the project list. Back to Projects / Projects stay
 * available — they are not map items.
 *
 * When no project is open, door titles stay muted names only (#971).
 * Do not replace them or add an under-label helper line
 * ("Open a project first." / "Open a book first." / …).
 */

export const NAV_WHY_OPEN_BOOK = 'Open a book first.';
export const NAV_WHY_OPEN_PROJECT = 'Open a project first.';
export const NAV_WHY_OPEN_BOOK_OR_SERIES = 'Open a book or a series first.';

export function isProjectsLanding(pathname) {
  const path = pathname || '';
  return path === '/projects' || path === '/projects/' || path === '/';
}

/**
 * A project is open when the writer has entered one that still exists
 * and they have not gone Back to Projects.
 */
export function isProjectOpen({
  currentProject = null,
  projects = null,
  pathname = '',
} = {}) {
  if (isProjectsLanding(pathname)) return false;
  if (!currentProject?.id) return false;
  if (Array.isArray(projects) && projects.length > 0) {
    return projects.some((p) => p.id === currentProject.id);
  }
  return true;
}

export function navDisabledWhy(item, {
  hasStory = false,
  currentProject = null,
  currentStory = null,
  canCreateSession = false,
  projects = null,
  pathname = '',
} = {}) {
  if (!item) return null;

  const projectOpen = isProjectOpen({ currentProject, projects, pathname });

  if (!projectOpen) {
    if (item.needsStory) return NAV_WHY_OPEN_BOOK;
    if (item.kind === 'create') return NAV_WHY_OPEN_BOOK_OR_SERIES;
    return NAV_WHY_OPEN_PROJECT;
  }

  if (item.needsStory && !hasStory) return NAV_WHY_OPEN_BOOK;
  if (item.needsProject && !currentProject) return NAV_WHY_OPEN_PROJECT;
  if (item.kind === 'create' && !canCreateSession) return NAV_WHY_OPEN_BOOK;
  if (item.kind === 'discovery' && !currentProject && !currentStory) {
    return NAV_WHY_OPEN_PROJECT;
  }
  return null;
}

/**
 * Visible under-label why. Blank when no project is open (#971).
 * When a project is open, book-needed doors still show why (#915).
 */
export function navWhyLine(item, args = {}) {
  if (!isProjectOpen(args)) return null;
  return navDisabledWhy(item, args);
}
